import React, { useEffect, useState } from 'react';
import { Alert, Typography, Divider, Button, Col, Row } from 'antd';
import { LoadingOutlined, ExpandOutlined } from '@ant-design/icons';
import { useTableauEmbedToken } from '../../core/modules/book/hooks/useTableauEmbedToken';
import { TABLEAU_EMBED_SCRIPT_SRC, TABLEAU_AUTHOR_PERFORMANCE_VIEW_URL } from '../../core/constants/global-constants';

const { Text, Title } = Typography;

interface ITableauEmbedProps {
  authorName?: string;
  height?: number | string;
  width?: number | string;
}

let scriptPromise: Promise<void> | null = null;

const loadTableauScript = (): Promise<void> => {
  if (scriptPromise) {
    return scriptPromise;
  }

  if (document.querySelector(`script[src="${TABLEAU_EMBED_SCRIPT_SRC}"]`)) {
    scriptPromise = Promise.resolve();
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TABLEAU_EMBED_SCRIPT_SRC;
    script.type = 'module';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load Tableau Embedding API script'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const TableauEmbed: React.FC<ITableauEmbedProps> = ({
  authorName,
  height = 500,
  width = '100%'
}) => {
  const { token, isLoading: isTokenLoading, isFetching: isTokenFetching, isError: isTokenError }
    = useTableauEmbedToken();
  const [scriptReady, setScriptReady] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const reportTitle = authorName
    ? `${authorName} - Past Titles Performance`
    : 'Past Titles Performance';

  // Load the Tableau Embedding API v3 script once per app session
  useEffect(() => {
    loadTableauScript()
      .then(() => setScriptReady(true))
      .catch(() => setScriptError(true));
  }, []);

  // Block render while any fetch (initial or background refresh) is in progress
  if (isTokenLoading || isTokenFetching || !scriptReady) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <LoadingOutlined style={{ fontSize: '40px' }} />
      </div>
    );
  }

  // ── Error: script failed to load ──────────────────────────────────────────
  if (scriptError) {
    return (
      <Alert
        type='error'
        showIcon
        message='Tableau script failed to load'
        description='Could not reach the Tableau Cloud instance. Check your network connection or refresh the page.'
        style={{ margin: '12px 0' }}
      />
    );
  }

  // ── Token failed (user has no Tableau access) ──────────────────────────────
  if (isTokenError || !token) {
    return (
      <Alert
        type='warning'
        showIcon
        message='Dashboard unavailable'
        description={
          <span>
            Could not authenticate with Tableau.{' '}
            <Text type='secondary'>
              Your account may not have Tableau access, or the Connected App
              may not be configured yet. Contact your Tableau admin.
            </Text>
          </span>
        }
        style={{ margin: '12px 0' }}
      />
    );
  }

  const dashboardContent = (
    <div
      style={
        isExpanded
          ? {
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: '#fff',
            padding: '16px',
            overflow: 'hidden'
          }
          : {
            width,
            overflow: 'hidden'
          }
      }
    >
      <Row
        justify='space-between'
        align='middle'
        style={{ marginBottom: '12px' }}
      >
        <Col>
          {isExpanded && (
            <Title level={4} style={{ margin: 0 }}>
              {reportTitle}
            </Title>
          )}
        </Col>

        <Col>
          {isExpanded && (
            <Button onClick={() => setIsExpanded(false)}>
            Close
            </Button>
          )}
        </Col>
      </Row>

      <tableau-viz
        src={TABLEAU_AUTHOR_PERFORMANCE_VIEW_URL}
        token={token}
        toolbar='hidden'
        hide-tabs='true'
        width='100%'
        height={isExpanded ? '95vh' : String(height)}
      >
        {authorName && (
          <viz-filter
            field='Author'
            value={authorName}
          />
        )}
      </tableau-viz>
    </div>
  );

  return (
    <>
      <Divider style={{ margin: '14px 0' }} />

      <Row
        justify='space-between'
        align='middle'
        style={{ margin: '16px 0 8px 0' }}
      >
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            {reportTitle}
          </Title>
        </Col>

        <Col>
          <Button
            type='text'
            size='small'
            icon={<ExpandOutlined />}
            onClick={() => setIsExpanded(true)}
          />
        </Col>
      </Row>

      {dashboardContent}
    </>
  );
};

export default TableauEmbed;